import { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    await setDoc(doc(db, "users", result.user.uid), {
      email,
      displayName,
      createdAt: new Date().toISOString(),
      settings: {
        startBal: 50000,
        beCap: 35,
        ratingType: "stars",
        userName: displayName
      }
    });
    return result;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  async function googleLogin() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    if (!result) return;

    const docRef = doc(db, "users", result.user.uid);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      await setDoc(docRef, {
        email: result.user.email,
        displayName: result.user.displayName,
        createdAt: new Date().toISOString(),
        settings: {
          startBal: 50000,
          beCap: 35,
          ratingType: "stars",
          userName: result.user.displayName
        }
      });
    }
    return result;
  }

  async function getUserSettings() {
    if (!currentUser) return null;
    const docSnap = await getDoc(doc(db, "users", currentUser.uid));
    if (docSnap.exists()) return docSnap.data().settings;
    return null;
  }

  async function updateUserSettings(settings) {
    if (!currentUser) return;
    await setDoc(doc(db, "users", currentUser.uid), { settings }, { merge: true });
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    googleLogin,
    getUserSettings,
    updateUserSettings
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}